import { openClientAppAsTenant } from "@/components/onboarding/client-app-link";
import { Button } from "@/components/ui/button";
import { Permissions } from "@/constants/permissions";
import { useSetupStatus } from "@/hooks/use-onboarding";
import { cn } from "@/lib/utils";
import { clientService } from "@/services/api/client-service";
import { useAdminStore } from "@/stores/admin-store";
import { useAdminUserContextStore } from "@/stores/admin-user-context-store";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { ArrowLeftRight, Building2, ExternalLink, LogOut } from "lucide-react";
import { useState } from "react";
import {
	CLIENT_ACCENT,
	isClientRoute,
	useClientContextNavigation,
} from "./client-context";
import { SwitchClientDialog } from "./switch-client-dialog";

/**
 * Banner across the top of every client page (founder, 2026-09-14): which
 * client is being managed, its industry and go-live status, and the three
 * context actions — Switch client, Open client app, Exit to Global Admin.
 * Status lookups fail soft (the banner just omits what it cannot load).
 */
export function ClientContextBanner() {
	const { pathname } = useLocation();
	const selectedClientId = useAdminStore((s) => s.selectedClientId);
	const selectedClientName = useAdminStore((s) => s.selectedClientName);
	const permissions = useAdminUserContextStore((s) => s.context.permissions);
	const canImpersonate = permissions.includes(
		Permissions.Clients.Impersonate,
	);
	const { exitClient } = useClientContextNavigation();
	const [switchOpen, setSwitchOpen] = useState(false);

	const visible = !!selectedClientId && isClientRoute(pathname);

	const client = useQuery({
		queryKey: ["client", selectedClientId],
		queryFn: () => clientService.getClientById(selectedClientId ?? ""),
		enabled: visible,
		retry: false,
	});
	const setup = useSetupStatus(
		visible && canImpersonate ? selectedClientId : null,
	);

	if (!visible) return null;

	const name = client.data?.clientName ?? selectedClientName ?? "Client";
	const ready = setup.data?.isReadyForGoLive;

	return (
		<>
			<div
				className={cn(
					"px-8 py-2.5 flex items-center justify-between gap-4 flex-wrap shadow-md",
					CLIENT_ACCENT.band,
					CLIENT_ACCENT.bandText,
				)}
			>
				<div className="flex items-center gap-3 min-w-0 flex-wrap">
					<Building2 className="h-5 w-5 shrink-0" />
					<span className={cn("text-sm", CLIENT_ACCENT.bandMuted)}>
						Managing
					</span>
					<span className="font-semibold text-base truncate">
						{name}
					</span>
					{client.data?.industryName && (
						<span
							className={cn(
								"text-xs rounded-full px-2 py-0.5",
								CLIENT_ACCENT.bandChip,
								CLIENT_ACCENT.bandMuted,
							)}
						>
							{client.data.industryName}
						</span>
					)}
					{client.data && !client.data.isActive && (
						<span className="text-xs rounded-full px-2 py-0.5 bg-slate-900/50 text-white">
							Inactive
						</span>
					)}
					{setup.data && (
						<span
							className={cn(
								"text-xs font-semibold rounded-full px-2 py-0.5",
								ready
									? "bg-emerald-100 text-emerald-800"
									: "bg-amber-100 text-amber-900",
							)}
							title={
								ready
									? "Every readiness gate is green or a warning."
									: "At least one readiness gate is red — see Onboarding."
							}
						>
							{ready
								? "Ready for go-live"
								: "Not ready for go-live"}
						</span>
					)}
				</div>
				<div className="flex items-center gap-2 flex-wrap">
					<Button
						type="button"
						size="sm"
						variant="outline"
						className={CLIENT_ACCENT.bannerButton}
						onClick={() => setSwitchOpen(true)}
					>
						<ArrowLeftRight className="mr-1.5 h-3.5 w-3.5" />
						Switch client
					</Button>
					{canImpersonate && selectedClientId && (
						<Button
							type="button"
							size="sm"
							variant="outline"
							className={CLIENT_ACCENT.bannerButton}
							onClick={() =>
								openClientAppAsTenant(selectedClientId)
							}
						>
							<ExternalLink className="mr-1.5 h-3.5 w-3.5" />
							Open client app
						</Button>
					)}
					<Button
						type="button"
						size="sm"
						variant="outline"
						className={CLIENT_ACCENT.bannerButton}
						onClick={exitClient}
					>
						<LogOut className="mr-1.5 h-3.5 w-3.5" />
						Exit to Global Admin
					</Button>
				</div>
			</div>
			<SwitchClientDialog
				open={switchOpen}
				onOpenChange={setSwitchOpen}
			/>
		</>
	);
}
